import React from 'react';

import { InlineAlert } from '@rbilabs/universal-components';
import { Pressable, StyleSheet, View } from 'react-native';

import { UnavailableOfferFooter } from 'experiments/deeplinks-flow-feature/treatment/components/offer-list-item/components/unavailable-offer-footer';
import { primitive, spacing } from 'styles/constants';

import { OfferContent, OfferHeader, OfferImage } from './components';
import { StackedCardContainer } from './components/stacked-card-container';
import { useOfferListItem } from './hooks/use-offer-list-item';
import type { OfferListItemProps } from './types';

export const OfferListItem = ({
  description,
  serviceModeStyle,
  serviceModeText,
  handleRemove,
  header,
  id,
  image,
  imageAlt,
  imageUri,
  offerApplied,
  offerText,
  onPress,
  promoCodeLabel,
  removeOfferText,
  unavailable,
  unavailableMessage,
  unavailableBadgeLabel,
  deeplinkUnavailablePresentation = false,
  onUnavailableActionPress,
  unavailableIcon,
  isWalmartOffer,
  isWalmartUser,
  isWalmartPaidMember,
  endDate,
  expirationDate,
  engineId,
  offerKind,
  ...buttonProps
}: OfferListItemProps) => {
  const {
    isHovered,
    setIsHovered,
    offersCooldown,
    expiresAt,
    configOfferDateRange,
    isVisible,
    showWalmartBadge,
    offerIsApplied,
    walmartBadgeImage,
  } = useOfferListItem({
    id,
    offerApplied,
    offerText,
    handleRemove,
    removeOfferText,
    isWalmartOffer,
    isWalmartUser,
    endDate,
    expirationDate,
  });

  if (!isVisible) {
    return null;
  }

  const {
    accessible: accessibleProp,
    style,
    accessibilityState,
    accessibilityRole = 'button',
    disabled: disabledProp,
    focusable: focusableProp,
    tabIndex: tabIndexProp,
    isGroup,
    isPartnershipOffer,
    activateButtonLabel,
    onPartnershipActivate,
    linkedButtonLabel,
    ...restButtonProps
  } = buttonProps as any;

  const pressedAvailableColor = '#F5F5F5';
  const pressedUnavailableColor = '#E0D9CC';

  const isUnavailable = Boolean((unavailable && unavailableMessage) || offersCooldown.isActive);
  const showDeeplinkUnavailableChrome = Boolean(
    deeplinkUnavailablePresentation &&
      unavailable &&
      (unavailableMessage || onUnavailableActionPress)
  );
  const showLegacyUnavailableAlert = Boolean(
    unavailable && unavailableMessage && !showDeeplinkUnavailableChrome
  );
  const hasUnavailableFooter = showDeeplinkUnavailableChrome || showLegacyUnavailableAlert;
  const hasNestedRemovalAction = offerIsApplied;
  const hasNestedUnavailableAction = Boolean(
    showDeeplinkUnavailableChrome && onUnavailableActionPress
  );
  const isNavigationBlocked = isUnavailable || Boolean(disabledProp);
  // Keep parent enabled when a nested Tap-here CTA must receive presses.
  // A disabled parent Pressable swallows touches to UnavailableOfferFooter.
  const isParentDisabled = Boolean(
    disabledProp || (isUnavailable && !hasNestedRemovalAction && !hasNestedUnavailableAction)
  );
  const shouldYieldAccessibilityToNestedAction =
    (isUnavailable && hasNestedRemovalAction) || hasNestedUnavailableAction;
  const parentAccessibilityRole = shouldYieldAccessibilityToNestedAction
    ? undefined
    : accessibilityRole;

  const content = (
    <View
      style={[
        !isGroup && styles.contentContainer,
        !isGroup &&
          (offerIsApplied ? styles.contentContainerApplied : styles.contentContainerDefault),
        !isGroup && (hasUnavailableFooter ? styles.borderBottomZero : styles.borderBottomRadius),
      ]}
    >
      <OfferHeader
        offerApplied={offerApplied}
        offerText={offerText}
        handleRemove={handleRemove}
        removeOfferText={removeOfferText}
      />
      <View style={styles.mainContent}>
        <OfferImage image={image} imageUri={imageUri} imageAlt={imageAlt} />
        <OfferContent
          header={header}
          description={description}
          isHovered={isHovered}
          expiresAt={expiresAt}
          configOfferDateRange={configOfferDateRange}
          showWalmartBadge={!!showWalmartBadge}
          walmartBadgeImage={walmartBadgeImage}
          promoCodeLabel={promoCodeLabel}
          serviceModeText={serviceModeText}
          serviceModeStyle={serviceModeStyle}
          isGroup={isGroup}
          isPartnershipOffer={
            buttonProps.isPartnershipOffer ?? (buttonProps as any).isPartnershipOffer
          }
          activateButtonLabel={buttonProps.activateButtonLabel}
          onPartnershipActivate={buttonProps.onPartnershipActivate}
          linkedButtonLabel={buttonProps.linkedButtonLabel}
          isWalmartUser={isWalmartUser}
          showUnavailableBadge={showDeeplinkUnavailableChrome}
          unavailableBadgeLabel={unavailableBadgeLabel}
        />
      </View>
    </View>
  );

  const unavailableFooter = !offerApplied ? (
    showDeeplinkUnavailableChrome ? (
      <UnavailableOfferFooter
        message={unavailableMessage}
        onPress={onUnavailableActionPress ?? undefined}
      />
    ) : showLegacyUnavailableAlert ? (
      <InlineAlert
        borderBottomRadius={10}
        borderTopRadius="0"
        width="full"
        status={unavailableIcon || 'info'}
        message={unavailableMessage}
        w="full"
      />
    ) : null
  ) : null;

  return (
    <Pressable
      accessible={shouldYieldAccessibilityToNestedAction ? false : accessibleProp}
      dd-action-name={header}
      disabled={isParentDisabled}
      focusable={shouldYieldAccessibilityToNestedAction ? false : focusableProp}
      tabIndex={shouldYieldAccessibilityToNestedAction ? -1 : tabIndexProp}
      onPress={!isNavigationBlocked ? onPress : undefined}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      dataSet={{
        sanityId: id,
        engineId,
        availability: unavailable ? 'unavailable' : 'available',
        // What adding this offer does (menu item, combo/picker, or an
        // order-level discount) and whether it is currently applied. Both are
        // part of the rendered contract automation selects offers by.
        offerKind,
        applied: offerIsApplied ? 'true' : 'false',
      }}
      style={({ pressed }) => {
        const pressedColor = isUnavailable ? pressedUnavailableColor : pressedAvailableColor;

        const baseStyles = [
          styles.container,
          isGroup
            ? style
            : [
                styles.offerListItemBox,
                isUnavailable && styles.offerListItemBoxUnavailable,
                offerApplied && styles.offerListItemBoxApplied,
                style,
                pressed && { backgroundColor: pressedColor },
              ],
        ];

        if (isGroup && pressed && onPress) {
          baseStyles.push({ opacity: 0.7 });
        }

        return baseStyles;
      }}
      {...restButtonProps}
      accessibilityRole={parentAccessibilityRole}
      accessibilityState={{ ...accessibilityState, disabled: isParentDisabled }}
      testID="offer-list-item"
    >
      {isGroup ? (
        <>
          <StackedCardContainer
            isActive={isUnavailable}
            isFocused={false}
            padding={
              buttonProps.isPartnershipOffer
                ? [`${spacing.$0}`, `${spacing.$0}`]
                : [`${spacing.$3}`, `${spacing.$4}`]
            }
          >
            {content}
          </StackedCardContainer>
          {unavailableFooter}
        </>
      ) : (
        <>
          {content}
          {unavailableFooter}
        </>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 10,
    width: '100%',
  },
  offerListItemBox: {
    backgroundColor: primitive.$white,
    borderRadius: 10,
    borderWidth: 0,
    borderColor: 'transparent',
  },
  offerListItemBoxUnavailable: {
    backgroundColor: primitive.$disabledNoOpacity,
  },
  offerListItemBoxApplied: {
    borderWidth: 2,
    borderColor: primitive.$bbqBrown,
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: 'transparent',
  },
  contentContainerApplied: {
    paddingTop: 4,
  },
  contentContainerDefault: {
    paddingTop: 12,
  },
  borderBottomRadius: {
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  borderBottomZero: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  mainContent: {
    flexDirection: 'row',
    marginRight: 8,
  },
});
